import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import CloseIcon from '@rsuite/icons/Close';
import HomeIcon from '@rsuite/icons/legacy/Home';
import CheckIcon from '@rsuite/icons/Check';
import ArrowRightIcon from '@rsuite/icons/ArrowRight';

interface Category {
    _id?: string;
    id?: string;
    name: string | { en: string; fa: string; [key: string]: string };
    togroup?: Category[];
}

interface HierarchicalCategorySelectorProps {
    categories: Category[];
    selectedId?: string;
    onChange: (categoryId: string) => void;
    className?: string;
}

export default function HierarchicalCategorySelector({
    categories,
    selectedId,
    onChange,
    className = ''
}: HierarchicalCategorySelectorProps) {
    const [currentPath, setCurrentPath] = useState<Category[]>([]);
    const [displayedCategories, setDisplayedCategories] = useState<Category[]>(categories || []);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);

    // Get category ID
    const getCategoryId = (category: Category): string => {
        return category._id || category.id || '';
    };

    // Get category name (bilingual: EN & FA)
    const getCategoryName = (name: any): { en: string; fa: string } => {
        if (typeof name === 'string') {
            return { en: name, fa: name };
        }
        if (typeof name === 'object') {
            return {
                en: name.en || name.fa || '',
                fa: name.fa || name.en || ''
            };
        }
        return { en: '', fa: '' };
    };

    // Flatten all categories for search
    const flattenCategories = useCallback((cats: Category[], parent: Category[] = []): any[] => {
        if (!cats || !Array.isArray(cats)) return [];
        
        const flatten = (c: Category[], p: Category[]): any[] => {
            return c.flatMap((cat) => {
                const current = {
                    ...cat,
                    path: [...p, cat]
                };
                const children = cat.togroup && Array.isArray(cat.togroup) && cat.togroup.length > 0
                    ? flatten(cat.togroup, [...p, cat])
                    : [];
                return [current, ...children];
            });
        };
        
        return flatten(cats, parent);
    }, []);

    // Initialize flattened categories for search
    useEffect(() => {
        if (categories && Array.isArray(categories)) {
            setAllCategories(flattenCategories(categories));
            setDisplayedCategories(categories);
        }
    }, [categories, flattenCategories]);

    // Handle search
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setIsSearching(false);
            setDisplayedCategories(currentPath.length > 0 
                ? currentPath[currentPath.length - 1].togroup || [] 
                : categories
            );
        } else {
            setIsSearching(true);
            const query = searchQuery.toLowerCase();
            const filtered = allCategories.filter((cat: any) => {
                const names = getCategoryName(cat.name);
                return names.en.toLowerCase().includes(query) || 
                       names.fa.toLowerCase().includes(query);
            });
            setDisplayedCategories(filtered);
        }
    }, [searchQuery, allCategories, currentPath, categories]);

    // Navigate to category (show its subcategories)
    const navigateToCategory = (category: Category) => {
        if (category.togroup && Array.isArray(category.togroup) && category.togroup.length > 0) {
            setCurrentPath([...currentPath, category]);
            setDisplayedCategories(category.togroup);
            setSearchQuery('');
        } else {
            // Leaf category - select it
            handleSelect(category);
        }
    };

    // Go back to parent
    const goBack = () => {
        if (currentPath.length === 0) return;
        
        const newPath = currentPath.slice(0, -1);
        setCurrentPath(newPath);
        
        if (newPath.length === 0) {
            setDisplayedCategories(categories);
        } else {
            setDisplayedCategories(newPath[newPath.length - 1].togroup || []);
        }
        setSearchQuery('');
    };

    // Go to root
    const goToRoot = () => {
        setCurrentPath([]);
        setDisplayedCategories(categories);
        setSearchQuery('');
    };

    // Navigate to specific level in breadcrumb
    const navigateToBreadcrumb = (index: number) => {
        if (index === -1) {
            goToRoot();
            return;
        }
        
        const newPath = currentPath.slice(0, index + 1);
        setCurrentPath(newPath);
        setDisplayedCategories(newPath[index].togroup || []);
        setSearchQuery('');
    };

    // Handle category selection
    const handleSelect = (category: Category) => {
        const id = getCategoryId(category);
        if (id) {
            onChange(id);
        }
    };

    // Navigate to search result's parent
    const navigateToSearchResult = (category: any) => {
        if (category.path && Array.isArray(category.path)) {
            const pathWithoutLast = category.path.slice(0, -1);
            setCurrentPath(pathWithoutLast);
            
            if (pathWithoutLast.length === 0) {
                setDisplayedCategories(categories);
            } else {
                setDisplayedCategories(pathWithoutLast[pathWithoutLast.length - 1].togroup || []);
            }
            setSearchQuery('');
            
            // Select the category
            handleSelect(category);
        } else {
            handleSelect(category);
        }
    };

    return (
        <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
            {/* Search Bar */}
            <div className="p-3 border-b border-gray-200">
                <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
                    <Input
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                        placeholder="جستجو در دسته‌بندی‌ها... / Search categories..."
                        className="w-full pl-10 pr-3"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            title="پاک کردن جستجو / Clear search"
                            aria-label="Clear search"
                        >
                            <CloseIcon style={{ width: 16, height: 16 }} />
                        </button>
                    )}
                </div>
            </div>

            {/* Breadcrumb Navigation */}
            {!isSearching && currentPath.length > 0 && (
                <div className="p-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-1 flex-wrap text-sm">
                        <button
                            type="button"
                            onClick={goToRoot}
                            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200 transition-colors"
                        >
                            <HomeIcon style={{ width: 12, height: 12 }} />
                            <span>همه دسته‌ها</span>
                        </button>
                        {currentPath.map((cat, index) => {
                            const names = getCategoryName(cat.name);
                            return (
                                <React.Fragment key={getCategoryId(cat)}>
                                    <ArrowRightIcon style={{ width: 12, height: 12 }} className="text-gray-400" />
                                    <button
                                        type="button"
                                        onClick={() => navigateToBreadcrumb(index)}
                                        className="px-2 py-1 rounded hover:bg-gray-200 transition-colors"
                                    >
                                        <span className="font-medium">{names.fa}</span>
                                        <span className="text-gray-500 text-xs ml-1">({names.en})</span>
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Back Button */}
            {!isSearching && currentPath.length > 0 && (
                <div className="p-2 border-b border-gray-200">
                    <Button
                        onClick={goBack}
                        size="sm"
                        appearance="subtle"
                        className="w-full justify-start"
                    >
                        <span style={{ transform: 'rotate(180deg)', display: 'inline-block', marginLeft: 8 }}>
                            <ArrowRightIcon style={{ width: 16, height: 16 }} />
                        </span>
                        بازگشت
                    </Button>
                </div>
            )}

            {/* Category List */}
            <div className="max-h-96 overflow-y-auto">
                {displayedCategories && displayedCategories.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                        {displayedCategories.map((cat: any) => {
                            const id = getCategoryId(cat);
                            const names = getCategoryName(cat.name);
                            const hasChildren = cat.togroup && Array.isArray(cat.togroup) && cat.togroup.length > 0;
                            const isSelected = id === selectedId;

                            return (
                                <div
                                    key={id}
                                    className={`p-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            {isSearching && cat.path ? (
                                                // Search result with path
                                                <button
                                                    type="button"
                                                    onClick={() => navigateToSearchResult(cat)}
                                                    className="w-full text-left"
                                                >
                                                    <div className="font-medium text-gray-900 truncate">
                                                        {names.fa}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate">
                                                        {names.en}
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {cat.path.map((p: Category, idx: number) => {
                                                            const pNames = getCategoryName(p.name);
                                                            return (
                                                                <span key={idx}>
                                                                    {idx > 0 && ' › '}
                                                                    {pNames.fa}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </button>
                                            ) : (
                                                // Normal navigation
                                                <button
                                                    type="button"
                                                    onClick={() => hasChildren ? navigateToCategory(cat) : handleSelect(cat)}
                                                    className="w-full text-left"
                                                >
                                                    <div className="font-medium text-gray-900 truncate">
                                                        {names.fa}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate">
                                                        {names.en}
                                                    </div>
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {isSelected && (
                                                <CheckIcon style={{ width: 16, height: 16 }} className="text-blue-600" />
                                            )}
                                            {hasChildren && !isSearching && (
                                                <ArrowRightIcon style={{ width: 16, height: 16 }} className="text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-400">
                        {searchQuery ? 'نتیجه‌ای یافت نشد / No results found' : 'دسته‌بندی موجود نیست'}
                    </div>
                )}
            </div>

            {/* Selected Category Info */}
            {selectedId && (
                <div className="p-3 bg-blue-50 border-t border-blue-200">
                    <div className="text-xs text-blue-600 mb-1">دسته انتخاب شده:</div>
                    {(() => {
                        const selected = allCategories.find((c: any) => getCategoryId(c) === selectedId);
                        if (selected) {
                            const names = getCategoryName(selected.name);
                            return (
                                <div className="text-sm font-medium text-blue-900">
                                    {names.fa} ({names.en})
                                </div>
                            );
                        }
                        return <div className="text-sm text-blue-600">ID: {selectedId}</div>;
                    })()}
                </div>
            )}
        </div>
    );
}
